import React, { memo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

/** Design ratio: on the 320-wide detail card, the label column ("Dominant Hand:", the
 *  longest label) measures 110 — i.e. 34.375% of the row. Kept as a % (not a fixed px)
 *  so it scales with the card instead of a guessed absolute width. */
const DETAIL_LABEL_WIDTH_PERCENT = `${(110 / 320) * 100}%`;

export type PlayerProfileDetailRow = { label: string; value: string };

export type PlayerProfileDetailProps = {
  /** Player photo; falls back to the placeholder avatar when absent. */
  avatarUrl?: string | null;
  name: string;
  level: number;
  /** Fill color for the level pill (callers pass `levelColor(level)`). */
  levelBadgeColor: string;
  /** Pre-formatted age (e.g. "8 years old"); hidden when null/empty. */
  ageLabel?: string | null;
  /** Label/value rows already filtered to the fields the API provided. */
  rows: PlayerProfileDetailRow[];
  style?: StyleProp<ViewStyle>;
};

/**
 * Read-only "Player Profile Detail" summary: a centered avatar, name, level pill + age,
 * and a label/value card. Purely presentational — callers build the rows and wrap it in
 * whatever chrome they need (a bottom sheet, a popup, etc.). Shared by the co-guardian
 * mutual-kid sheet and the onboarding "View player" sheet.
 */
function PlayerProfileDetail({
  avatarUrl,
  name,
  level,
  levelBadgeColor,
  ageLabel,
  rows,
  style,
}: PlayerProfileDetailProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack style={style} gap={scale(20)}>
      <Stack align="center" gap={scale(12)}>
        <Avatar size={scale(120)} borderWidth={scale(2)} borderColor={colors['0']}>
          <Avatar.Image
            source={avatarUrl ? { uri: avatarUrl } : imgUser}
            backgroundColor={guardianColors['2']}
          />
        </Avatar>
        <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.name}>
          {name}
        </Text>
        <Stack direction="row" align="center" gap={scale(13)}>
          <Stack style={[styles.levelPill, { backgroundColor: levelBadgeColor }]}>
            <Text variant="Font Inter/Subtitle/Bold 10px" style={styles.levelPillText}>
              {STRINGS.guardian.home.levelBadge.replace('{level}', String(level))}
            </Text>
          </Stack>
          {ageLabel ? (
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.ageText}>
              {ageLabel}
            </Text>
          ) : null}
        </Stack>
      </Stack>

      {rows.length > 0 ? (
        <Stack style={styles.detailCard} gap={scale(12)}>
          {rows.map(row => (
            <Stack key={row.label} direction="row">
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.detailLabel}>
                {row.label}
              </Text>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.detailValue}>
                {row.value}
              </Text>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default memo(PlayerProfileDetail);

const useStyles = createStyles(({ scale }) => ({
  name: {
    color: guardianColors['10'],
  },
  levelPill: {
    paddingHorizontal: scale(8),
    paddingVertical: scale(4),
    borderRadius: scale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  levelPillText: {
    color: colors['0'],
  },
  ageText: {
    color: guardianColors['10'],
  },
  detailCard: {
    backgroundColor: guardianColors['1'],
    borderRadius: scale(8),
    borderCurve: 'continuous',
    padding: scale(12),
  },
  detailLabel: {
    width: DETAIL_LABEL_WIDTH_PERCENT,
    color: guardianColors['6'],
  },
  detailValue: {
    flex: 1,
    color: guardianColors['10'],
  },
}));
